import { apiClient } from '@/core/api/client';
import { trackRumAction } from '@/shared/observability/rum';
import { buildSelectionPayload } from '../selection';
import { getGenerationState, patchCategories } from './navigatorGenerationStore';

// Sessions with a swap POST still in flight — concurrent clicks are rejected
// outright (no optimistic churn, no competing resets server-side).
const inFlightSessions = new Set<string>();

/**
 * Feature 080 — swap the selected provider for one specialty (single-select).
 *
 * Optimistic: the store is updated first (the UI morphs immediately), then the
 * full single-select map is POSTed — the backend resets every selection for
 * the session before re-marking, so the payload must carry all specialties.
 * On failure the previous snapshot is restored and `false` returned so the
 * caller can surface a toast/message. One swap per session at a time.
 */
export const swapSelection = async (
  sessionId: string,
  specialtyId: string,
  providerId: string,
): Promise<boolean> => {
  const state = getGenerationState(sessionId);
  if (!state || inFlightSessions.has(sessionId)) return false;
  inFlightSessions.add(sessionId);

  const previous = state.categories;
  const next = previous.map((specialty) =>
    specialty.specialty_id === specialtyId
      ? {
          ...specialty,
          providers: specialty.providers.map((provider) => ({
            ...provider,
            user_selected: provider.provider_id === providerId,
          })),
        }
      : specialty,
  );

  patchCategories(sessionId, next);
  trackRumAction('care_guide_selection_swapped', { sessionId, specialtyId, providerId });

  try {
    await apiClient.post('/find-providers/care-plan', {
      session_id: sessionId,
      selected_providers: buildSelectionPayload(next),
    });
    return true;
  } catch {
    patchCategories(sessionId, previous);
    return false;
  } finally {
    inFlightSessions.delete(sessionId);
  }
};
